import { Either, Option, ReadonlyArray } from "@morphism/fp";
import React from "react";

import * as GenericDriver from "~/Driver";
import { DVIR, DVIRs } from "~/DVIR";
import { ID } from "~/ID";
import { Mutable } from "~/Mutable";
import { Order } from "~/Order";
import { Select as GenericSelect } from "~/Select";
import { Branch } from "./Branch";

export namespace Driver {
  export const column = ({
    dvirs,
    drivers,
    onChange,
  }: {
    // eslint-disable-next-line functional/prefer-readonly-type
    readonly dvirs: DVIR[];
    readonly drivers?: readonly ID[];
    readonly onChange: (drivers?: readonly ID[]) => void;
  }) => ({
    title: "Driver",
    dataIndex: "driver",
    filteredValue: drivers?.[0] && ["driver"],
    onFilter: (_: unknown, dvir: DVIR) => pipe(dvir, getFilterFunc(drivers)),
    filterDropdown: (props: { readonly confirm: () => void }) => (
      <Filter {...{ dvirs, drivers, onChange }} {...props} />
    ),

    sorter: (a: DVIR, b: DVIR) =>
      Order.optionally(GenericDriver.compare)(
        Either.getOrElseW(() => undefined)(a.driver),
        Either.getOrElseW(() => undefined)(b.driver)
      ),

    render: (_: unknown, dvir: DVIR) => (
      <Branch.BranchTooltip
        driver={dvir.driver}
      />
    ),
  });

  export const getFilterFunc = (drivers: readonly ID[] = []) => (dvir: DVIR) =>
    pipe(
      dvir.driver,
      Either.fold(
        () => false,
        ({ id }) => !drivers.length || drivers.map(({ id }) => id).includes(id)
      )
    );

  export const getFilterFuncForSettings = (settings: DVIRs.Table.Settings) =>
    getFilterFunc(settings.drivers);

  const Filter = ({
    dvirs,
    loading,
    drivers,
    onChange,
    confirm,
  }: DVIRs.Table.Filter.Props & Parameters<typeof column>[0]) => {
    const selected = (drivers ?? []).map(({ id }) => id);
    const options = pipe(
      dvirs,
      ReadonlyArray.filterMap(
        flow(
          ({ driver }) => driver,
          Either.fold(
            () => Option.none,
            (driver) =>
              Option.some({
                key: driver.id,
                value: GenericDriver.toString(driver),
                ...driver,
              })
          )
        )
      ),
      ReadonlyArray.uniq({ equals: ({ key: a }, { key: b }) => a === b }),
      ReadonlyArray.sort(GenericDriver.order())
    );

    const value = pipe(
      options,
      ReadonlyArray.filterMap(({ key, value }) =>
        selected.includes(key) ? Option.some(value) : Option.none
      )
    );

    return (
      <>
        <DVIRs.Table.Filter
          confirm={confirm}
          onReset={drivers?.[0] && onChange}
        >
          <GenericSelect
            loading={loading}
            style={{ width: 200 }}
            dropdownMatchSelectWidth
            placeholder="Filter by drivers"
            mode="multiple"
            bordered={false}
            options={options as Mutable<typeof options>}
            value={value as Mutable<typeof value>}
            onChange={
              ((_value: never, selected: readonly { readonly key: string }[]) =>
                onChange(selected.map(({ key: id }) => ({ id })))) as never
            }
          />
        </DVIRs.Table.Filter>
      </>
    );
  };
}
